import React, { useState, useEffect } from "react";
import { Plus } from "lucide-react";
import { CircularProgress } from "@mui/material";
import DashboardForm from "./DashboardForm";
import DashboardTable from "./DashboardTable";

export default function DashboardManagement({
  dashboards,
  regions,
  awsAccounts,
  loading,
  error,
  setError,
  fetchNamespaces,
  fetchMetrics,
  fetchResourceNames,
  saveDashboard,
  deleteDashboard,
}) {
  const [isAddingDashboard, setIsAddingDashboard] = useState(false);
  const [isEditingDashboard, setIsEditingDashboard] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isUserAction, setIsUserAction] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    name: "",
    accountNo: "",
    region: "",
    namespaces: [],
  });

  // Start adding new dashboard

  const startAddDashboard = () => {
    setDashboardData({
      name: "",
      accountNo: "",
      region: "",
      namespaces: [],
    });
    setCurrentStep(1);
    setIsAddingDashboard(true);
    setIsEditingDashboard(null);
    setIsUserAction(true);
  };

  // Start editing dashboard

  const startEditDashboard = (dashboard) => {
    setDashboardData({ ...dashboard });
    setCurrentStep(1);
    setIsAddingDashboard(false);
    setIsEditingDashboard(dashboard.id);
    setIsUserAction(true);
  };

  // Cancel dashboard add/edit

  const cancelDashboardOperation = () => {
    setIsAddingDashboard(false);
    setIsEditingDashboard(null);
    setCurrentStep(1);
    // Keep isUserAction true when canceling operations
  };

  // Handle save of dashboard

  const handleSaveDashboard = async () => {
    const success = await saveDashboard(dashboardData, isEditingDashboard);
    if (success) {
      cancelDashboardOperation();
    }
  };

  // Handle delete of dashboard

  const handleDeleteDashboard = async (dashboardId) => {
    setIsUserAction(true);

    await deleteDashboard(dashboardId);
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header */}
      <div className="flex justify-between items-center p-6 border-b border-gray-200">
        <h1 className="text-2xl font-bold text-gray-800">
          Dashboard Management
        </h1>
        <button
          onClick={startAddDashboard}
          className={`flex items-center gap-2 px-4 py-2 bg-[#003366] text-white rounded-md hover:bg-[#004488] transition-colors ${isAddingDashboard ? "opacity-50 cursor-not-allowed" : ""
            }`}
          disabled={isAddingDashboard}
        >
          <Plus size={18} />
          Create Dashboard
        </button>
      </div>

      {/* Error message */}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-4 rounded mx-6 mt-4">
          <p>{error}</p>
          <button
            className="text-red-600 underline text-sm ml-2"
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex flex-col">
        {/* Dashboard Creation/Editing Form - Now inside the main container */}

        {isAddingDashboard && (
          <div className="border-b border-gray-200">
            <DashboardForm
              dashboards={dashboards}
              isAddingDashboard={isAddingDashboard}
              isEditingDashboard={isEditingDashboard}
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
              dashboardData={dashboardData}
              setDashboardData={setDashboardData}
              regions={regions}
              awsAccounts={awsAccounts}
              fetchNamespaces={fetchNamespaces}
              fetchMetrics={fetchMetrics}
              fetchResourceNames={fetchResourceNames}
              onSave={handleSaveDashboard}
              onCancel={cancelDashboardOperation}
            />
          </div>
        )}

        {/* Dashboard Editing Form - Separate from Adding form for clarity */}

        {isEditingDashboard !== null && (
          <div className="border-b border-gray-200">
            <DashboardForm
              isAddingDashboard={false}
              isEditingDashboard={isEditingDashboard}
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
              dashboardData={dashboardData}
              setDashboardData={setDashboardData}
              regions={regions}
              awsAccounts={awsAccounts}
              fetchNamespaces={fetchNamespaces}
              fetchMetrics={fetchMetrics}
              fetchResourceNames={fetchResourceNames}
              onSave={handleSaveDashboard}
              onCancel={cancelDashboardOperation}
            />
          </div>
        )}

        {/* Dashboards Table with integrated loading state */}
        <div
          className={`${isAddingDashboard || isEditingDashboard !== null ? "mt-4" : ""}`}
        >
          {loading && !isUserAction ? (
            <div className="flex justify-center items-center p-8">
              <CircularProgress size={40} style={{ color: "#003366" }} />
            </div>
          ) : (
            <DashboardTable
              dashboards={dashboards}
              regions={regions}
              awsAccounts={awsAccounts}
              onEdit={startEditDashboard}
              onDelete={handleDeleteDashboard}
            />
          )}
        </div>
      </div>
    </div>
  );
}
